import { buildCycleAudiencePayloadIfHydrated } from './audiencePayload';
import { buildCycleEvaluationPeriodPayload } from './periodPayload';
import { hasCompletePeriod } from './validation';

import type { CycleAudiencePayload } from '@/api/cycle-evaluation-services/cycle-evaluation-group/cycleEvaluationGroupMutations';
import type { CycleAudienceCriteriaTypeValue } from '@/constants/cycle-evaluation/CycleAudienceCriteria';
import type { CycleAudienceTypeValue } from '@/constants/cycle-evaluation/CycleEvaluation';
import type { SelectedFilters } from '@/hooks/filters/useCompanyAttributesFilters';
import type { CycleEvaluationGroupPeriodOverride } from '@/navigation/CycleEvaluation/cycleEvaluationGroupSteps';
import type { EvaluationPeriodOption } from '@/pages/ManagerAssessment/Evaluation/types';
import type { CycleEvaluationGroup } from '@/types/cycle-evaluation/CycleEvaluationGroup';

import { CycleAudienceType } from '@/constants/cycle-evaluation/CycleEvaluation';
import { CUSTOM_PERIOD_VALUE } from '@/pages/ManagerAssessment/Evaluation/components/EvaluationDatePeriod';

export interface CycleInformationDraft {
    name: string;
    selectedPeriod: string;
    periodStart?: Date;
    periodEnd?: Date;
    audienceType: CycleAudienceTypeValue;
    filters: SelectedFilters;
}

export interface CycleInformationAutosavePayload {
    name: string;
    period?: CycleEvaluationGroupPeriodOverride;
    audience?: CycleAudiencePayload;
}

interface AudienceCriteriaLike {
    criteriaType: CycleAudienceCriteriaTypeValue;
    customAttributeId?: string | null;
    value: string;
}

interface BuildCycleInformationAutosavePayloadParams {
    draft: CycleInformationDraft;
    group: CycleEvaluationGroup;
    periods: EvaluationPeriodOption[];
    offeredPeriodValues: readonly string[];
    isAudienceHydrated: boolean;
}

interface CycleInformationDraftComparison {
    group: CycleEvaluationGroup;
    payload: CycleInformationAutosavePayload;
}

// The group update is a full replacement: an incomplete period is left out, never sent as nulls.
const buildCycleInformationAutosavePayload = ({
    draft,
    group,
    periods,
    offeredPeriodValues,
    isAudienceHydrated,
}: BuildCycleInformationAutosavePayloadParams): CycleInformationAutosavePayload => {
    const { name, selectedPeriod, periodStart, periodEnd, audienceType, filters } = draft;

    const period = hasCompletePeriod({
        selectedPeriod,
        periodStart,
        periodEnd,
        offeredPeriodValues,
    })
        ? buildCycleEvaluationPeriodPayload({ selectedPeriod, periodStart, periodEnd, periods })
        : undefined;

    const audience = buildCycleAudiencePayloadIfHydrated({
        isAudienceHydrated,
        audienceType,
        filters,
        persistedCriterias: group.audience?.cycleAudienceCriterias ?? [],
    });

    return { name: name.trim(), period, audience };
};

// A custom range carries no preset id: the form spells it `custom`, the server leaves it empty.
const toPresetPeriodId = (periodId?: string | null): string | null =>
    !periodId || periodId === CUSTOM_PERIOD_VALUE ? null : periodId;

const isSameInstant = (left?: string | null, right?: string | null): boolean =>
    (left ? new Date(left).getTime() : null) === (right ? new Date(right).getTime() : null);

const toAudienceSignature = ({
    audienceType,
    criterias,
}: {
    audienceType: CycleAudienceTypeValue;
    criterias: AudienceCriteriaLike[];
}): string =>
    [
        audienceType,
        ...criterias
            .map(
                ({ criteriaType, customAttributeId, value }) =>
                    `${criteriaType}:${customAttributeId ?? ''}:${value}`,
            )
            .sort(),
    ].join('|');

const hasPeriodChanged = ({
    group,
    period,
}: {
    group: CycleEvaluationGroup;
    period?: CycleEvaluationGroupPeriodOverride;
}): boolean => {
    if (!period) {
        return false;
    }

    const presetId = toPresetPeriodId(period.evaluationPeriodId);

    return (
        presetId !== toPresetPeriodId(group.evaluationPeriodId) ||
        !isSameInstant(period.evaluationPeriodStart, group.evaluationPeriodStart) ||
        !isSameInstant(period.evaluationPeriodEnd, group.evaluationPeriodEnd)
    );
};

const hasAudienceChanged = ({
    group,
    audience,
}: {
    group: CycleEvaluationGroup;
    audience?: CycleAudiencePayload;
}): boolean => {
    if (!audience) {
        return false;
    }

    return (
        toAudienceSignature({
            audienceType: audience.cycleAudienceType,
            criterias: audience.cycleAudienceCriterias,
        }) !==
        toAudienceSignature({
            audienceType: group.audience?.cycleAudienceType ?? CycleAudienceType.ENTIRE_COMPANY,
            criterias: group.audience?.cycleAudienceCriterias ?? [],
        })
    );
};

const hasCycleInformationDraftChanged = ({
    group,
    payload,
}: CycleInformationDraftComparison): boolean =>
    payload.name !== group.name ||
    hasPeriodChanged({ group, period: payload.period }) ||
    hasAudienceChanged({ group, audience: payload.audience });

export { buildCycleInformationAutosavePayload, hasCycleInformationDraftChanged };
